import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  IconButton,
  Tooltip,
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  Paper,
  Divider,
} from '@mui/material';
import {
  Search,
  Download,
  Refresh,
  CheckCircle,
  X,
  Visibility,
  FilterList,
  CalendarToday,
  AccountBalanceWallet,
  Upload,
  Description,
  Warning,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { API_URL } from './config';

const PaymentReconciliationPanel = () => {
  const [reconciliationData, setReconciliationData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [selectedItems, setSelectedItems] = useState([]);
  const [alert, setAlert] = useState({ show: false, type: 'info', message: '' });
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedReconciliation, setSelectedReconciliation] = useState(null);
  const [bankStatementDialogOpen, setBankStatementDialogOpen] = useState(false);

  // Fetch reconciliation data
  const fetchReconciliationData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (dateRange.start) params.append('startDate', dateRange.start.toISOString());
      if (dateRange.end) params.append('endDate', dateRange.end.toISOString());

      const response = await fetch(`${API_URL}/reconciliation/payments?${params}`);
      const data = await response.json();

      if (data.status === 200) {
        setReconciliationData(data.reconciliation || []);
      } else {
        // Use demo data if API fails
        setReconciliationData(getDemoReconciliationData());
      }
    } catch (error) {
      console.error('Error fetching reconciliation data:', error);
      setReconciliationData(getDemoReconciliationData());
    } finally {
      setLoading(false);
    }
  };

  // Demo reconciliation data
  const getDemoReconciliationData = () => [
    {
      id: 1,
      withdrawal_id: 123,
      driver_name: 'John Driver',
      driver_id: 'DRV-001',
      amount: 250.00,
      withdrawal_date: '2025-02-01T10:30:00',
      approved_date: '2025-02-01T11:15:00',
      status: 'matched',
      bank_transaction_id: 'BANK-789',
      bank_date: '2025-02-01T14:20:00',
      bank_amount: 250.00,
      reconciliation_date: '2025-02-02T09:00:00',
      reconciled_by: 'admin',
      notes: 'Payment confirmed in bank statement'
    },
    {
      id: 2,
      withdrawal_id: 124,
      driver_name: 'Jane Driver',
      driver_id: 'DRV-002',
      amount: 180.50,
      withdrawal_date: '2025-02-01T12:45:00',
      approved_date: '2025-02-01T13:30:00',
      status: 'unmatched',
      bank_transaction_id: null,
      bank_date: null,
      bank_amount: null,
      reconciliation_date: null,
      reconciled_by: null,
      notes: 'Awaiting bank confirmation'
    },
    {
      id: 3,
      withdrawal_id: 125,
      driver_name: 'Bob Driver',
      driver_id: 'DRV-003',
      amount: 320.75,
      withdrawal_date: '2025-02-01T15:20:00',
      approved_date: '2025-02-01T16:00:00',
      status: 'discrepancy',
      bank_transaction_id: 'BANK-791',
      bank_date: '2025-02-02T10:15:00',
      bank_amount: 325.75, // Different amount
      reconciliation_date: '2025-02-02T11:30:00',
      reconciled_by: 'admin',
      notes: 'Amount mismatch: Expected $320.75, Bank shows $325.75'
    }
  ];

  useEffect(() => {
    fetchReconciliationData();
  }, [statusFilter, dateRange]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'matched': return 'success';
      case 'unmatched': return 'warning';
      case 'discrepancy': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'matched': return <CheckCircle />;
      case 'unmatched': return <Warning />;
      case 'discrepancy': return <X />;
      default: return <Warning />;
    }
  };

  const handleSelectItem = (id) => {
    setSelectedItems(prev =>
      prev.includes(id)
        ? prev.filter(item => item !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedItems(filteredData.map(item => item.id));
    } else {
      setSelectedItems([]);
    }
  };

  const handleReconcile = async (withdrawalIds, action) => {
    try {
      const response = await fetch(`${API_URL}/reconciliation/process`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          withdrawal_ids: withdrawalIds,
          action: action,
          processed_by: 'admin'
        })
      });

      const data = await response.json();
      
      if (data.status === 200) {
        setAlert({
          show: true,
          type: 'success',
          message: `Successfully ${action}d ${withdrawalIds.length} payments`
        });
        fetchReconciliationData();
        setSelectedItems([]);
      } else {
        throw new Error(data.message || 'Failed to process reconciliation');
      }
    } catch (error) {
      console.error('Error processing reconciliation:', error);
      setAlert({
        show: true,
        type: 'error',
        message: `Failed to ${action} payments: ${error.message}`
      });
    }
  };

  const handleViewDetails = (item) => {
    setSelectedReconciliation(item);
    setDetailsDialogOpen(true);
  };

  const handleBankStatementUpload = (file) => {
    // Handle bank statement upload
    console.log('Bank statement uploaded:', file);
    setBankStatementDialogOpen(false);
    setAlert({
      show: true,
      type: 'success',
      message: 'Bank statement uploaded successfully'
    });
    fetchReconciliationData();
  };

  const exportReconciliation = () => {
    const csvContent = [
      ['ID', 'Withdrawal ID', 'Driver Name', 'Driver ID', 'Amount', 'Withdrawal Date', 'Status', 'Bank Transaction ID', 'Bank Date', 'Bank Amount', 'Notes'],
      ...filteredData.map(item => [
        item.id,
        item.withdrawal_id,
        item.driver_name,
        item.driver_id,
        item.amount,
        new Date(item.withdrawal_date).toLocaleString(),
        item.status,
        item.bank_transaction_id || '',
        item.bank_date ? new Date(item.bank_date).toLocaleString() : '',
        item.bank_amount || '',
        item.notes || ''
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payment_reconciliation_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const filteredData = reconciliationData.filter(item => {
    const matchesSearch = item.driver_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.driver_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.withdrawal_id?.toString().includes(searchTerm);
    const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: reconciliationData.length,
    matched: reconciliationData.filter(item => item.status === 'matched').length,
    unmatched: reconciliationData.filter(item => item.status === 'unmatched').length,
    discrepancy: reconciliationData.filter(item => item.status === 'discrepancy').length,
    totalAmount: reconciliationData.reduce((sum, item) => sum + (item.amount || 0), 0)
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 4, p: 3, bgcolor: 'primary.50', borderRadius: 2, border: '1px solid', borderColor: 'primary.200' }}>
          <Typography variant="h4" gutterBottom sx={{ color: 'primary.900' }}>
            🏦 Payment Reconciliation
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
            Match approved withdrawals with bank statements to ensure payment accuracy.
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <Chip 
              icon={<Description />} 
              label="Upload Bank Statement" 
              color="primary" 
              variant="outlined" 
              size="small" 
              onClick={() => setBankStatementDialogOpen(true)}
              clickable
            />
            <Chip 
              icon={<CheckCircle />} 
              label="Match Payments" 
              color="success" 
              variant="outlined" 
              size="small" 
            />
            <Chip 
              icon={<Warning />} 
              label="Review Discrepancies" 
              color="error" 
              variant="outlined" 
              size="small" 
            />
          </Box>
        </Box>

        {/* Status Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h6" color="text.primary">
                  {stats.total}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  📋 Total Payments
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h6" color="success.dark">
                  {stats.matched}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ✅ Matched
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.200' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h6" color="warning.dark">
                  {stats.unmatched}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ⏳ Unmatched
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'error.50', border: '1px solid', borderColor: 'error.200' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h6" color="error.dark">
                  ${stats.totalAmount.toFixed(2)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  💰 Total Amount
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {alert.show && (
          <Alert 
            severity={alert.type} 
            sx={{ mb: 2 }}
            onClose={() => setAlert({ ...alert, show: false })}
          >
            {alert.message}
          </Alert>
        )}

        {/* Filters and Actions */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="🔍 Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />
              }}
              helperText="Search by driver name, ID, or withdrawal ID"
            />
          </Grid>
          
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>📊 Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">📋 All Status</MenuItem>
                <MenuItem value="matched">✅ Matched</MenuItem>
                <MenuItem value="unmatched">⏳ Unmatched</MenuItem>
                <MenuItem value="discrepancy">❌ Discrepancy</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <DatePicker
              label="📅 Start Date"
              value={dateRange.start}
              onChange={(newValue) => setDateRange({ ...dateRange, start: newValue })}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <DatePicker
              label="📅 End Date"
              value={dateRange.end}
              onChange={(newValue) => setDateRange({ ...dateRange, end: newValue })}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<Refresh />}
                onClick={fetchReconciliationData}
                disabled={loading}
                sx={{ flex: 1 }}
              >
                🔄 Refresh
              </Button>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={exportReconciliation}
                disabled={filteredData.length === 0}
                sx={{ flex: 1 }}
              >
                📥 Export
              </Button>
            </Box>
          </Grid>
        </Grid>

        {/* Bulk Actions */}
        {selectedItems.length > 0 && (
          <Paper sx={{ p: 2, mb: 2, bgcolor: 'info.50', border: '1px solid', borderColor: 'info.200' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2">
                {selectedItems.length} items selected
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  onClick={() => handleReconcile(selectedItems, 'match')}
                >
                  ✅ Mark as Matched
                </Button>
                <Button
                  variant="outlined"
                  color="warning"
                  size="small"
                  onClick={() => handleReconcile(selectedItems, 'unmatch')}
                >
                  ⏳ Mark as Unmatched
                </Button>
              </Box>
            </Box>
          </Paper>
        )}

        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {/* Reconciliation Table */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                📋 Payment Reconciliation ({filteredData.length})
              </Typography>
              <Typography variant="body2" color="text.secondary">
                💡 Match withdrawals with bank transactions
              </Typography>
            </Box>
            
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedItems.length > 0 && selectedItems.length < filteredData.length}
                        checked={selectedItems.length === filteredData.length}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>🆔 ID</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>👤 Driver</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>💰 Amount</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>📅 Withdrawal Date</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>📊 Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>🏦 Bank Transaction</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>💳 Bank Amount</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>⚡ Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData.map((item) => (
                    <TableRow 
                      key={item.id}
                      sx={{ 
                        '&:hover': { bgcolor: 'action.hover' },
                        bgcolor: item.status === 'unmatched' ? 'warning.50' : 
                               item.status === 'discrepancy' ? 'error.50' : 'inherit'
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedItems.includes(item.id)}
                          onChange={() => handleSelectItem(item.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          #{item.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            👤 {item.driver_name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            🆔 {item.driver_id}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                          💵 ${item.amount?.toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            📅 {new Date(item.withdrawal_date).toLocaleDateString()}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            🕐 {new Date(item.withdrawal_date).toLocaleTimeString()}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(item.status)}
                          label={item.status?.toUpperCase()}
                          color={getStatusColor(item.status)}
                          size="small"
                          sx={{ fontWeight: 'bold' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          {item.bank_transaction_id ? (
                            <>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                🏦 {item.bank_transaction_id}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                📅 {item.bank_date ? new Date(item.bank_date).toLocaleDateString() : 'N/A'}
                              </Typography>
                            </>
                          ) : (
                            <Typography variant="caption" color="textSecondary">
                              ⏳ Not matched
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ 
                          fontWeight: 'bold',
                          color: item.bank_amount !== item.amount ? 'error.main' : 'success.main'
                        }}>
                          💵 ${item.bank_amount?.toFixed(2) || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                          <Tooltip title="🔍 View reconciliation details">
                            <IconButton
                              size="small"
                              onClick={() => handleViewDetails(item)}
                              sx={{ bgcolor: 'info.50', '&:hover': { bgcolor: 'info.100' } }}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          
                          {item.status === 'unmatched' && (
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Tooltip title="✅ Mark as matched">
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() => handleReconcile([item.id], 'match')}
                                  sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' } }}
                                >
                                  <CheckCircle fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            {filteredData.length === 0 && !loading && (
              <Box sx={{ textAlign: 'center', py: 6, bgcolor: 'grey.50', borderRadius: 2 }}>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  📭 No reconciliation data found
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Upload a bank statement to start reconciling payments.
                </Typography>
                <Button 
                  variant="outlined" 
                  onClick={() => setBankStatementDialogOpen(true)} 
                  startIcon={<Upload />}
                >
                  📤 Upload Bank Statement
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Details Dialog */}
        <Dialog 
          open={detailsDialogOpen} 
          onClose={() => setDetailsDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ bgcolor: 'primary.50', color: 'primary.900' }}>
            🏦 Reconciliation Details - #{selectedReconciliation?.id}
          </DialogTitle>
          <DialogContent>
            {selectedReconciliation && (
              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <Card sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      👤 Withdrawal Information
                    </Typography>
                    <Typography variant="body2">🆔 Withdrawal ID: {selectedReconciliation.withdrawal_id}</Typography>
                    <Typography variant="body2">👤 Driver: {selectedReconciliation.driver_name}</Typography>
                    <Typography variant="body2">🆔 Driver ID: {selectedReconciliation.driver_id}</Typography>
                    <Typography variant="body2">💰 Amount: ${selectedReconciliation.amount?.toFixed(2)}</Typography>
                    <Typography variant="body2">📅 Date: {new Date(selectedReconciliation.withdrawal_date).toLocaleString()}</Typography>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Card sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      🏦 Bank Transaction
                    </Typography>
                    <Typography variant="body2">🏦 Transaction ID: {selectedReconciliation.bank_transaction_id || 'Not matched'}</Typography>
                    <Typography variant="body2">💳 Bank Amount: ${selectedReconciliation.bank_amount?.toFixed(2) || 'N/A'}</Typography>
                    <Typography variant="body2">📅 Bank Date: {selectedReconciliation.bank_date ? new Date(selectedReconciliation.bank_date).toLocaleString() : 'N/A'}</Typography>
                    <Typography variant="body2">📊 Status: {selectedReconciliation.status?.toUpperCase()}</Typography>
                  </Card>
                </Grid>
                
                <Grid item xs={12}>
                  <Card sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      📝 Notes & Audit Trail
                    </Typography>
                    <Typography variant="body2">📝 Notes: {selectedReconciliation.notes || 'No notes available'}</Typography>
                    <Typography variant="body2">👤 Reconciled By: {selectedReconciliation.reconciled_by || 'Not reconciled'}</Typography>
                    <Typography variant="body2">📅 Reconciliation Date: {selectedReconciliation.reconciliation_date ? new Date(selectedReconciliation.reconciliation_date).toLocaleString() : 'Not reconciled'}</Typography>
                  </Card>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, bgcolor: 'grey.50' }}>
            <Button onClick={() => setDetailsDialogOpen(false)} variant="outlined">
              ❌ Close
            </Button>
            {selectedReconciliation?.status === 'unmatched' && (
              <Button 
                color="success" 
                onClick={() => {
                  handleReconcile([selectedReconciliation.id], 'match');
                  setDetailsDialogOpen(false);
                }}
                variant="contained"
                startIcon={<CheckCircle />}
              >
                ✅ Mark as Matched
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Bank Statement Upload Dialog */}
        <Dialog 
          open={bankStatementDialogOpen} 
          onClose={() => setBankStatementDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ bgcolor: 'primary.50', color: 'primary.900' }}>
            📤 Upload Bank Statement
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="body1" gutterBottom>
                Upload your bank statement to automatically match payments
              </Typography>
              
              <Box 
                sx={{ 
                  border: '2px dashed', 
                  borderColor: 'primary.main', 
                  borderRadius: 2, 
                  p: 4, 
                  mt: 2,
                  bgcolor: 'primary.50',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'primary.100' }
                }}
                onClick={() => document.getElementById('bank-statement-file').click()}
              >
                <Upload sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" color="primary.main">
                  Drop bank statement here
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  or click to browse
                </Typography>
                <input
                  id="bank-statement-file"
                  type="file"
                  accept=".csv,.xlsx,.pdf"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      handleBankStatementUpload(file);
                    }
                  }}
                />
              </Box>
              
              <Typography variant="caption" color="textSecondary" sx={{ mt: 2 }}>
                Supported formats: CSV, Excel, PDF
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, bgcolor: 'grey.50' }}>
            <Button onClick={() => setBankStatementDialogOpen(false)} variant="outlined">
              ❌ Cancel
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default PaymentReconciliationPanel;
